import React, { useState } from 'react'

import { useMutation } from '@apollo/client'

import { client } from '../../../../apollo'
import { CREATE_TENANT_OAUTH_CONFIG } from '../../../../graphql/tenant'

type FormState = {
  name: string
  tenantId: string
  issuerUrl: string
  slug: string
  scope: string[]
  clientId: string
  clientSecret: string
  isProduction: boolean
}

export default function CreateTenantOAuthConfigForm() {
  const [formData, setFormData] = useState<FormState>({
    name: '',
    tenantId: '',
    issuerUrl: '',
    slug: '',
    scope: ['id', 'email'],
    clientId: '',
    clientSecret: '',
    isProduction: false,
  })

  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)
  const [configId, setConfigId] = useState<number | null>(null)

  const [createTenantOAuthConfig, { loading }] = useMutation(
    CREATE_TENANT_OAUTH_CONFIG,
    {
      onCompleted: (data) => {
        const result = data.adminCreateTenantOauthConfig
        if (result.statusCode === 200) {
          setSuccess(true)
          setConfigId(result.configId)
        } else {
          setError(result.error || 'An unknown error occurred')
        }
      },
      onError: (err) => {
        setError(err.message || 'Failed to create tenant OAuth config')
      },
      client,
    },
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    try {
      await createTenantOAuthConfig({
        variables: {
          name: formData.name,
          tenantId: formData.tenantId,
          issuerUrl: formData.issuerUrl,
          slug: formData.slug,
          scope: formData.scope,
          clientId: formData.clientId,
          clientSecret: formData.clientSecret,
          isProduction: formData.isProduction,
        },
      })
    } catch (_err: any) {
      // Error handling is done in onError callback
    }
  }
  return (
    <div>
      {success && (
        <div>
          Tenant OAuth Config created successfully!
          {configId && <p>Config ID: {configId}</p>}
        </div>
      )}

      {error && <div>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div>
          <label htmlFor="name">
            <strong>Name</strong> (End-user facing name for OAuth option)
            <br />
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="tenantId">
            <strong>Tenant ID</strong>
            <br />
            <input
              type="text"
              id="tenantId"
              name="tenantId"
              value={formData.tenantId}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="issuerUrl">
            <strong>Issuer URL</strong>
            <br />
            <input
              type="url"
              id="issuerUrl"
              name="issuerUrl"
              value={formData.issuerUrl}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="slug">
            <strong>Slug</strong>
            <br />
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="scope">
            <strong>Scope</strong>
            <br />
            <input
              type="text"
              id="scope"
              name="scope"
              value={formData.scope.join(',')}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  scope: e.target.value.split(','),
                }))
              }
              required
            />
          </label>
          &nbsp; comma-separated (current:{' '}
          {`[${formData.scope.map((x) => `"${x}"`).join(', ')}]`})
        </div>

        <div>
          <label htmlFor="clientId">
            <strong>Client ID</strong>
            <br />
            <input
              type="text"
              id="clientId"
              name="clientId"
              value={formData.clientId}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="clientSecret">
            <strong>Client Secret</strong>
            <br />
            <input
              type="password"
              id="clientSecret"
              name="clientSecret"
              value={formData.clientSecret}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="isProduction">
            <input
              type="checkbox"
              id="isProduction"
              name="isProduction"
              checked={formData.isProduction}
              onChange={handleInputChange}
            />{' '}
            <strong>Is Production</strong> (uncheck for staging/dev configs)
          </label>
        </div>

        <button type="submit" disabled={loading}>
          {loading ? 'Creating...' : 'Create Tenant OAuth Config'}
        </button>
      </form>
    </div>
  )
}
